import React from 'react';
import { Sparkles } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const MANUAL_KINDS = [['customer_dc', 'Customer DC'], ['transfer_out', 'Transfer to other site'], ['branch_transfer', 'Within-site transfer'], ['job_work', 'Job worker DC']];
const PARTY = [['legal_name', 'Party name (receiver)', 'md:col-span-2'], ['gstin', 'GSTIN', 'md:col-span-2'],
  ['address1', 'Address line 1', 'md:col-span-2'], ['address2', 'Address line 2', 'md:col-span-2'], ['place', 'Place', ''], ['pincode', 'PIN code', ''], ['state', 'State', 'md:col-span-2']];
export const sel = 'h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm';

export const Field = ({ label, className = '', children }) => <div className={`space-y-1 ${className}`}><Label className="text-xs text-gray-600">{label}</Label>{children}</div>;

export const AiNotes = ({ notes }) => (notes ? (
  <p className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="manual-ai-notes"><Sparkles className="w-4 h-4 shrink-0" />AI notes: {notes}</p>
) : null);

/** GSTIN box + 'Unregistered dealer' tick (GSTIN = URP). A blank GSTIN is never taken as unregistered. */
const GstinField = ({ party, onChange, prefix, label, className }) => {
  const urp = party.gstin === 'URP';
  return (
    <Field label={label} className={className}>
      <div className="flex items-center gap-3">
        <Input value={party.gstin || ''} disabled={urp} onChange={(e) => onChange('gstin', e.target.value.toUpperCase())} className="h-9 flex-1 font-mono disabled:cursor-default disabled:bg-amber-50 disabled:font-semibold disabled:text-amber-900 disabled:opacity-100" data-testid={`${prefix}-gstin`} />
        <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-gray-700">
          <Checkbox checked={urp} onCheckedChange={(v) => onChange('gstin', v ? 'URP' : '')} data-testid={`${prefix}-urp`} />Unregistered dealer
        </label>
      </div>
      {!party.gstin && <p className="text-[11px] text-amber-700" data-testid={`${prefix}-gstin-hint`}>Enter the GSTIN, or tick Unregistered dealer</p>}
    </Field>
  );
};

/** Name / GSTIN / address / PIN / state of one party — `prefix` keeps the test ids apart (manual-party / inward-party / supplier). */
export const PartyFields = ({ party, onChange, states, labels = {}, prefix = 'manual-party' }) => (
  <div className="grid md:grid-cols-4 gap-3">
    {PARTY.map(([k, l, span]) => (k === 'gstin' ? <GstinField key={k} party={party} onChange={onChange} prefix={prefix} label={labels[k] || l} className={span} /> : (
      <Field key={k} label={labels[k] || l} className={span}>
        <Input value={party[k] || ''} onChange={(e) => onChange(k, e.target.value)} list={k === 'state' ? `${prefix}-states` : undefined}
          className={`h-9 ${k === 'pincode' ? 'font-mono' : ''}`} data-testid={`${prefix}-${k}`} />
      </Field>
    )))}
    <datalist id={`${prefix}-states`}>{(states || []).map((s) => <option key={s} value={s} />)}</datalist>
  </div>
);

/** Manual DC header + receiver as the AI read it (or blank) — every field editable before saving. */
export const ManualDcForm = ({ f, setF, states }) => (
  <div className="space-y-3" data-testid="manual-form">
    <AiNotes notes={f.notes} />
    <div className="grid md:grid-cols-3 gap-3">
      <Field label="What is this DC?">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={sel} data-testid="manual-kind">
          {MANUAL_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Field label="DC number"><Input value={f.doc_no} onChange={(e) => setF({ ...f, doc_no: e.target.value })} maxLength={16} className="h-9 font-mono" data-testid="manual-doc-no" /></Field>
      <Field label="DC date"><Input type="date" value={f.doc_date} onChange={(e) => setF({ ...f, doc_date: e.target.value })} className="h-9" data-testid="manual-doc-date" /></Field>
    </div>
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <PartyFields party={f.to} onChange={(k, v) => setF({ ...f, to: { ...f.to, [k]: v } })} states={states} />
    </div>
  </div>
);

export default ManualDcForm;
